import { useMemo, useState } from 'react';
import type { LeagueStanding } from '../lib/api';

interface Props {
  scope: 'MINI' | 'OVERALL';
  leagueName: string;
  entries: LeagueStanding[];
  bramEntryId: number | null;
  rivalEntryId: number | null;
  bramReferenceTotal: number | null;
  onSelect(entry: LeagueStanding): void;
}

export function LeagueBoard({ scope, leagueName, entries, bramEntryId, rivalEntryId, bramReferenceTotal, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((entry) => `${entry.rank} ${entry.entry_name} ${entry.player_name} ${entry.entry}`.toLowerCase().includes(q));
  }, [entries, search]);
  const bram = entries.find((entry) => entry.entry === bramEntryId) ?? null;
  const referenceTotal = bram?.total ?? bramReferenceTotal;
  const isOverall = scope === 'OVERALL';

  return (
    <section className="panel overflow-hidden p-0">
      <div className="flex flex-col gap-3 border-b border-slate-800/90 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="eyebrow">{isOverall ? 'OVERALL FPL RADAR' : 'MINI-LEAGUE RADAR'} · TOP {entries.length}</div>
            <span className={isOverall ? 'mode-chip active' : 'mode-chip'}>{isOverall ? 'GLOBAL · LEAGUE 314' : 'PRIVATE/CLASSIC LEAGUE'}</span>
          </div>
          <h2 className="mt-1 text-lg font-black text-white">{leagueName || (isOverall ? 'Overall' : 'Classic league')}</h2>
          <p className="mt-1 text-xs text-slate-600">{isOverall ? 'De officiële FPL Overall-ranking. Klik VS BRAM om een Top-100-manager direct als rival te laden en de sim te vernieuwen.' : 'Jouw eigen mini-league. Klik VS BRAM om iedere manager direct als rival te laden en de sim te vernieuwen.'}</p>
        </div>
        <input className="input max-w-sm" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Zoek rank, team, manager of entry ID…" />
      </div>
      <div className="league-table-wrap">
        <table className="league-table">
          <thead>
            <tr><th>#</th><th>Team / manager</th><th>GW</th><th>Total</th><th>Gap Bram</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((entry) => {
              const isBram = entry.entry === bramEntryId;
              const isRival = entry.entry === rivalEntryId;
              const gap = referenceTotal === null ? null : entry.total - referenceTotal;
              return (
                <tr key={entry.entry} className={isBram ? 'row-bram' : isRival ? 'row-rival' : ''}>
                  <td className="rank-cell">{entry.rank}</td>
                  <td>
                    <div className="font-extrabold text-slate-200">{entry.entry_name}</div>
                    <div className="mt-0.5 text-[10px] text-slate-600">{entry.player_name} · ID {entry.entry}</div>
                  </td>
                  <td className="tabular-nums font-bold text-slate-300">{entry.event_total}</td>
                  <td className="tabular-nums font-black text-white">{entry.total}</td>
                  <td className={`tabular-nums font-bold ${gap === null ? 'text-slate-700' : gap > 0 ? 'text-rose-300' : gap < 0 ? 'text-emerald-300' : 'text-slate-400'}`}>{gap === null ? '—' : gap > 0 ? `+${gap}` : String(gap)}</td>
                  <td className="text-right">
                    {isBram ? <span className="badge-official">BRAM</span> : (
                      <button className={isRival ? 'mini-btn active' : 'mini-btn'} onClick={() => onSelect(entry)}>{isRival ? 'ACTIVE RIVAL' : 'VS BRAM'}</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-xs text-slate-600">Geen managers gevonden voor deze filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
